/**
 * ListGroup component for the Indigo design system.
 *
 * A bordered container that groups related rows: an optional header (title,
 * description and a trailing `meta` slot for a count, status or action) above
 * rows separated by dividers. Compose the rows with `ListGroupItem`.
 *
 * Variants:
 * - `shaded` (default) — the header sits on a shaded band, for groups whose
 *   rows are the content (e.g. a list of selectable items under a heading).
 * - `plain` — the header shares the white surface, for a titled panel whose
 *   body is free-form content (pass it as `children`).
 *
 * @example
 * ```tsx
 * <ListGroup title="Attachments" meta="2 of 3 included">
 *   <ListGroupItem
 *     leading={<Checkbox checked aria-label="Cover letter" />}
 *     title="Cover letter"
 *     description="PDF · 1 page"
 *     trailing={<Button variant="ghost" size="sm">Preview</Button>}
 *   />
 * </ListGroup>
 * ```
 */
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import {
  Children,
  forwardRef,
  type KeyboardEvent,
  type MouseEventHandler,
  type ReactNode,
  useId,
} from "react";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./ListGroup.css";

const listGroupVariants = cva("indigo-list-group", {
  variants: {
    variant: {
      shaded: "indigo-list-group--shaded",
      plain: "indigo-list-group--plain",
    },
  },
  defaultVariants: {
    variant: "shaded",
  },
});

export type ListGroupVariant = "shaded" | "plain";

export interface ListGroupProps {
  /** Group heading. Omit for a header-less list. */
  title?: ReactNode;

  /** Secondary line under the title (e.g. a role or an address). */
  description?: ReactNode;

  /** Trailing header slot — a count, a status or an action. */
  meta?: ReactNode;

  /** Header styling; see the component docs. @default "shaded" */
  variant?: ListGroupVariant;

  /** Rows (`ListGroupItem`) or, for `plain`, free-form body content. */
  children?: ReactNode;

  /** Additional CSS classes applied to the root element. */
  className?: string;

  /** Test id applied to the root element. */
  "data-testid"?: string;
}

export const ListGroup = forwardRef<HTMLElement, ListGroupProps>(
  (
    {
      title,
      description,
      meta,
      variant = "shaded",
      children,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const titleId = useId();
    const hasHeader = Boolean(title || description || meta);
    const hasBody = Children.toArray(children).length > 0;

    return (
      <section
        ref={ref}
        className={clsx(listGroupVariants({ variant }), className)}
        aria-labelledby={title ? titleId : undefined}
        data-testid={dataTestId}
      >
        {hasHeader && (
          <header
            className={clsx(
              "indigo-list-group__header",
              hasBody && "indigo-list-group__header--divided",
            )}
          >
            <div className="indigo-list-group__heading">
              {title && (
                <Heading
                  id={titleId}
                  level={variant === "plain" ? 3 : 4}
                  className="indigo-list-group__title"
                >
                  {title}
                </Heading>
              )}
              {description && (
                <Body
                  size={variant === "plain" ? "sm" : "xs"}
                  className="indigo-list-group__description text-indigo-text-tertiary"
                >
                  {description}
                </Body>
              )}
            </div>
            {meta && <div className="indigo-list-group__meta">{meta}</div>}
          </header>
        )}
        {hasBody && <div className="indigo-list-group__body">{children}</div>}
      </section>
    );
  },
);

ListGroup.displayName = "ListGroup";

export type ListGroupItemTone = "default" | "muted";

export interface ListGroupItemProps {
  /** Leading slot, typically a `Checkbox`. Aligned to the title line. */
  leading?: ReactNode;

  /**
   * A line above the title — a status or a label that frames the row, e.g. a
   * `PendingStatus` saying what will happen to it.
   */
  overline?: ReactNode;

  /** Row title. */
  title?: ReactNode;

  /** Inline addon after the title, typically a `Tag`. */
  titleAddon?: ReactNode;

  /** Secondary line under the title (metadata, a summary). */
  description?: ReactNode;

  /** Trailing slot — an action, a status or a link. */
  trailing?: ReactNode;

  /**
   * Free-form content. Rendered in place of `title` / `description` when they
   * are absent, or below them when they are set.
   */
  children?: ReactNode;

  /**
   * `muted` renders the row as a quiet note — for empty states and hints
   * inside a group ("Nothing to show for this person").
   * @default "default"
   */
  tone?: ListGroupItemTone;

  /** Highlights the row, e.g. while its details are open elsewhere. */
  selected?: boolean;

  /**
   * Click handler. When set, the whole row becomes a button — so use it only
   * for rows without their own controls; a row with a checkbox or an action
   * should leave selection to those controls.
   */
  onClick?: MouseEventHandler<HTMLDivElement>;

  /** Additional CSS classes applied to the row. */
  className?: string;

  /** Test id applied to the row. */
  "data-testid"?: string;
}

export const ListGroupItem = forwardRef<HTMLDivElement, ListGroupItemProps>(
  (
    {
      leading,
      overline,
      title,
      titleAddon,
      description,
      trailing,
      children,
      tone = "default",
      selected = false,
      onClick,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const interactive = Boolean(onClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      // Ignore keys aimed at a control inside the row (a checkbox, a button).
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.(event as unknown as Parameters<typeof onClick>[0]);
      }
    };

    return (
      <div
        ref={ref}
        role={interactive ? "button" : undefined}
        className={clsx(
          "indigo-list-group-item",
          tone === "muted" && "indigo-list-group-item--muted",
          selected && "indigo-list-group-item--selected",
          interactive && "indigo-list-group-item--interactive",
          className,
        )}
        aria-current={selected ? "true" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={interactive ? handleKeyDown : undefined}
        data-testid={dataTestId}
      >
        {leading && (
          <div className="indigo-list-group-item__leading">{leading}</div>
        )}
        <div className="indigo-list-group-item__content">
          {overline && (
            <div className="indigo-list-group-item__overline">{overline}</div>
          )}
          {(title || titleAddon) && (
            <div className="indigo-list-group-item__title-row">
              {title && (
                <Heading level={4} className="indigo-list-group-item__title">
                  {title}
                </Heading>
              )}
              {titleAddon}
            </div>
          )}
          {description && (
            <Body
              size="sm"
              className="indigo-list-group-item__description text-indigo-text-tertiary"
            >
              {description}
            </Body>
          )}
          {children}
        </div>
        {trailing && (
          <div className="indigo-list-group-item__trailing">{trailing}</div>
        )}
      </div>
    );
  },
);

ListGroupItem.displayName = "ListGroupItem";
